import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

/**
 * BLOC 19/10 — Formulaire "Demander un accès" (P0 LLDLS).
 *
 * Remplace le lien "Créer mon entreprise" sur la page login. Aucun compte créé.
 * Les demandes vont dans la collection super-admin et sont (optionnellement)
 * forwardées en lead dans le tenant Wérko si configuré.
 *
 * Protection : honeypot invisible (champ `website` display:none → bots) +
 * rate limit backend 1 req/IP/3600s.
 */
export default function AccessRequestPage() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    company_name: "",
    email: "",
    phone: "",
    trade: "",
    message: "",
    website: "", // honeypot
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const nav = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/public/access-request", {
        ...form,
        email: form.email.trim().toLowerCase(),
      });
      setSuccess(true);
      toast.success("Demande envoyée", {
        description: "Nous vous recontactons sous 24-48 h ouvrables.",
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError(
          "Vous avez déjà envoyé une demande récemment. Patientez une heure avant un nouvel essai.",
        );
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthShell
        title="Demande transmise"
        subtitle="Nous reviendrons vers vous rapidement."
        footer={
          <>
            Vous avez un code ?{" "}
            <Link to="/signup" className="font-medium text-[#C4553B] hover:underline">
              Créer mon compte
            </Link>
          </>
        }
      >
        <div
          className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-6 text-center"
          data-testid="access-request-success"
        >
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-400" />
          <p className="text-slate-100">
            Merci {form.first_name}. Votre demande pour <strong>{form.company_name}</strong> a été
            transmise à l&apos;équipe Wérko.
          </p>
          <p className="mt-2 text-sm text-slate-400">
            Nous vous enverrons un code d&apos;invitation sous 24-48 h ouvrables sur <strong>{form.email}</strong>.
          </p>
          <Button
            onClick={() => nav("/login")}
            className="mt-4 bg-[#C4553B] text-white hover:bg-[#A8402C]"
            data-testid="access-request-back-btn"
          >
            Retour à la connexion
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Demander un accès"
      subtitle="Wérko est accessible sur invitation. Décrivez-nous votre activité, nous revenons vers vous."
      footer={
        <>
          Déjà un compte ?{" "}
          <Link to="/login" className="font-medium text-[#C4553B] hover:underline">
            Se connecter
          </Link>
          {" · "}
          Code en main ?{" "}
          <Link to="/signup" className="font-medium text-[#C4553B] hover:underline">
            Créer mon compte
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="access-request-form">
        {/* Honeypot — champ hors écran, les bots le remplissent et sont filtrés */}
        <div
          aria-hidden="true"
          style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}
        >
          <label htmlFor="website">Site web (ne pas remplir)</label>
          <input
            type="text"
            id="website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={set("website")}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-slate-300">Prénom</Label>
            <Input
              required
              value={form.first_name}
              onChange={set("first_name")}
              className="border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="access-firstname"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Nom</Label>
            <Input
              required
              value={form.last_name}
              onChange={set("last_name")}
              className="border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="access-lastname"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Entreprise</Label>
          <Input
            required
            value={form.company_name}
            onChange={set("company_name")}
            placeholder="ToitPro SPRL"
            className="border-slate-700 bg-slate-950/40 text-slate-100"
            data-testid="access-company"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-slate-300">Email pro</Label>
            <Input
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              className="border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="access-email"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Téléphone</Label>
            <Input
              required
              value={form.phone}
              onChange={set("phone")}
              placeholder="+32 475 ..."
              className="border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="access-phone"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Métier principal</Label>
          <Input
            required
            value={form.trade}
            onChange={set("trade")}
            placeholder="Toiture, Photovoltaïque, Maçonnerie, ..."
            className="border-slate-700 bg-slate-950/40 text-slate-100"
            data-testid="access-trade"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Besoin, questions (optionnel)</Label>
          <Textarea
            value={form.message}
            onChange={set("message")}
            rows={3}
            maxLength={1000}
            className="border-slate-700 bg-slate-950/40 text-slate-100"
            data-testid="access-message"
          />
        </div>
        {error && (
          <div
            className="rounded border border-red-500/30 bg-red-500/10 p-2.5 text-sm text-red-300"
            data-testid="access-error"
          >
            {error}
          </div>
        )}
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-[#C4553B] text-white hover:bg-[#A8402C]"
          data-testid="access-submit"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Envoyer ma demande
        </Button>
      </form>
    </AuthShell>
  );
}
